import { AlertColor } from '@mui/material';
import Notification from 'components/Notification';
import { FormDataModel } from 'components/Form';
import { formatTimeForApi, parseTimeValue } from 'components/TimeInput/TimeInput';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { CountryCode } from 'libphonenumber-js';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import strings from '../../../common/Translation/Translate';
import axiosInstance from '../../../utils/axios';
import { URLS } from '../../../utils/constants/urls';
import { fetchFromStorage } from 'utils/storage';
import { createDropdownData } from 'utils/validators/HelperFunctions';
import MasterDataEntityDrawer from '../CommonPage/MasterDataEntityDrawer';
import { AddRfqForm } from './AddRfq/AddRfqForm';

dayjs.extend(isSameOrAfter);

const defaultFormValues = (): FormDataModel => ({
  eventId: '',
  eventSubtype: '',
  venueId: '',
  placeOfEvent: '',
  addressOfEvent: '',
  guests: '',
  pricePackageId: '',
  preferredStartDate: '',
  preferredEndDate: '',
  startTime: '',
  endTime: '',
  firstName: '',
  lastName: '',
  mobile: '',
  email: '',
  companyName: '',
});

interface RfqEntityDrawerProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

const RfqEntityDrawer: FC<RfqEntityDrawerProps> = ({ open, onClose, onSaved }) => {
  const defaultCountry = (fetchFromStorage('defaultCountryCode') ?? 'IN') as CountryCode;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [eventTypeList, setEventTypeList] = useState<any[]>([]);
  const [venueList, setVenueList] = useState<any[]>([]);
  const [pricePackageList, setPricePackageList] = useState<any[]>([]);
  const [pricePackageDisabled, setPricePackageDisabled] = useState(true);
  const [maxCapacity, setMaxCapacity] = useState(0);
  const [isOdc, setIsOdc] = useState(false);
  const [formValues, setFormValues] = useState<FormDataModel>(defaultFormValues());
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({
    display: false,
    severity: null,
    message: '',
  });

  const closeNotification = (value: boolean) => setMessage(prev => ({ ...prev, display: value }));

  const handleCatch = (error: any) => {
    setMessage({
      display: true,
      severity: 'error',
      message:
        error?.response?.data?.error_description ??
        error?.response?.data?.message ??
        error?.message ??
        'Something went wrong',
    });
  };

  const loadMasterData = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get(URLS.getEventAndVenuesRfq);
      setEventTypeList(createDropdownData(data.eventTypes, ['id', 'name']));
      setVenueList(createDropdownData(data.venues, ['id', 'name']));
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setLoading(false);
    }
  }, []);

  const getPricePackageList = useCallback(async (eventTypeId: number, venueId: number) => {
    try {
      setLoading(true);
      const { status, data } = await axiosInstance.get(URLS.getPricePackageRfq, {
        params: { eventTypeId, venueId },
      });
      if (status === 200) {
        setPricePackageList(createDropdownData(data.pricePackages, ['id', 'name']));
        setMaxCapacity(data.maximumBookCapacity);
        setIsOdc(Boolean(data.isOdc));
        setPricePackageDisabled(false);
      }
    } catch (error: any) {
      setPricePackageList([]);
      setPricePackageDisabled(true);
      handleCatch(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setFormValues(defaultFormValues());
    setPricePackageList([]);
    setPricePackageDisabled(true);
    setMaxCapacity(0);
    setIsOdc(false);
    loadMasterData();
  }, [open, loadMasterData]);

  const formModel = useMemo(
    () =>
      AddRfqForm(
        strings,
        defaultCountry,
        eventTypeList,
        venueList,
        pricePackageList,
        pricePackageDisabled,
        maxCapacity,
        isOdc,
      ),
    [defaultCountry, eventTypeList, venueList, pricePackageList, pricePackageDisabled, maxCapacity, isOdc],
  );

  const handleFieldChange = async (field: string, value: any, formData: FormDataModel) => {
    if (field === 'eventId' || field === 'venueId') {
      const eventId = field === 'eventId' ? value : formData.eventId;
      const venueId = field === 'venueId' ? value : formData.venueId;
      const nextValues = {
        ...formData,
        [field]: value,
        guests: '',
        pricePackageId: '',
      };
      setFormValues(nextValues);

      if (eventId && venueId) {
        await getPricePackageList(Number(eventId), Number(venueId));
      } else {
        setPricePackageList([]);
        setPricePackageDisabled(true);
        setIsOdc(false);
      }
      return;
    }

    if (field === 'preferredStartDate' && dayjs(value).isValid()) {
      setFormValues({
        ...formData,
        preferredStartDate: value,
        preferredEndDate: formData.preferredEndDate || value,
      });
      return;
    }

    setFormValues({ ...formData, [field]: value });
  };

  const buildPayload = (body: FormDataModel) => {
    if (dayjs(body.preferredStartDate as string).isAfter(dayjs(body.preferredEndDate as string))) {
      setMessage({
        display: true,
        severity: 'error',
        message: strings.common_endDate_error_message,
      });
      return null;
    }
    const start = parseTimeValue(body.startTime as string);
    const end = parseTimeValue(body.endTime as string);
    if (
      dayjs(body.preferredStartDate as string).isSame(dayjs(body.preferredEndDate as string)) &&
      start &&
      end &&
      (start.isSame(end) || start.isAfter(end))
    ) {
      setMessage({
        display: true,
        severity: 'error',
        message: strings.timePickerValidation,
      });
      return null;
    }

    return {
      ...body,
      preferredStartDate: dayjs(body.preferredStartDate as string).format('DD-MM-YYYY'),
      preferredEndDate: dayjs(body.preferredEndDate as string).format('DD-MM-YYYY'),
      startTime: formatTimeForApi(body.startTime as string),
      endTime: formatTimeForApi(body.endTime as string),
      mobile:
        body.mobile === '' || body.mobile == null
          ? { countryCode: null, value: '' }
          : body.mobile,
    };
  };

  const handleSave = async (body: FormDataModel) => {
    try {
      setSaving(true);
      const payload = buildPayload(body);
      if (!payload) return;
      await axiosInstance.post(URLS.addRfq, payload);
      setMessage({
        display: true,
        severity: 'success',
        message: strings.common_save_message,
      });
      onSaved();
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
          autoHideDuration={2000}
        />
      )}
      <MasterDataEntityDrawer
        open={open}
        title={strings.rfq_title_add}
        isEdit={false}
        formModel={formModel}
        formValues={formValues}
        onFieldChange={handleFieldChange}
        loading={loading}
        saving={saving}
        width="70%"
        onClose={onClose}
        onSave={handleSave}
        saveLabel="Save"
        onInvalid={(msg: string) => setMessage({ display: true, severity: 'error', message: msg })}
      />
    </>
  );
};

export default RfqEntityDrawer;
